import { z } from 'zod/v4';

import { CurrencyRuntype, MonetaryValueBackendRuntype } from '../backendTypes';
import type { DriverIdDTO } from '../driver/driverIdBackendTypes';
import { DriverIdRuntype } from '../driver/driverIdBackendTypes';
import { AggregatedAvailabilityRuntype } from '../offer/availablityBackendTypes';

// Lock mode enum for items and sections
export const LockModeRuntype = z.enum(['none', 'partial', 'full']);
export type LockModeDTO = z.infer<typeof LockModeRuntype>;
// Formula display unit enum
export type FormulaDisplayUnitDTO = z.infer<typeof FormulaDisplayUnitRuntype>;
export const FormulaDisplayUnitRuntype = z.enum(['percentage', 'number', 'dimensionless_number', 'monetary_value']);

// ========================================
// Evaluation endpoint types
// ========================================

// Request body for evaluating a calculation plan
export type EvaluateCalculationRequestDTO = z.infer<typeof EvaluateCalculationRequestRuntype>;
export const EvaluateCalculationRequestRuntype = z.object({
    id: z.string(), // UUID of the calculation plan
});

// Value for a single scenario (unit + total)
export type EvaluatedCalculationLineItemValueDTO = z.infer<typeof EvaluatedCalculationLineItemValueRuntype>;
export const EvaluatedCalculationLineItemValueRuntype = z.object({
    unit_value: MonetaryValueBackendRuntype.nullish(),
    total_value: MonetaryValueBackendRuntype,
});

// BTreeMap<Uuid, T> serializes to Record<string, T> in JSON
export type EvaluatedScenarioValuesDTO = z.infer<typeof EvaluatedScenarioValuesRuntype>;
export const EvaluatedScenarioValuesRuntype = z.record(
    z.string(), // Scenario UUID key
    EvaluatedCalculationLineItemValueRuntype,
);

// Breakdown item name enum (adjacently tagged: { type, data? })
export type BreakdownItemNameDTO = z.infer<typeof BreakdownItemNameRuntype>;
export const BreakdownItemNameRuntype = z.discriminatedUnion('type', [
    // Manufacturing process names
    z.object({ type: z.literal('ProcessPreparation') }),
    z.object({ type: z.literal('Npi') }),
    z.object({ type: z.literal('Smt') }),
    z.object({ type: z.literal('Tht') }),
    z.object({ type: z.literal('HandSoldering') }),
    z.object({ type: z.literal('PanelSeparation') }),
    z.object({ type: z.literal('Potting') }),
    z.object({ type: z.literal('Ruggedizing') }),
    z.object({ type: z.literal('Testing') }),
    z.object({ type: z.literal('Inspection') }),
    z.object({ type: z.literal('CableAssembly') }),
    z.object({ type: z.literal('MechanicalAssembly') }),
    z.object({ type: z.literal('QualityControl') }),
    z.object({ type: z.literal('Packaging') }),
    z.object({ type: z.literal('Logistics') }),
    z.object({ type: z.literal('Other') }),
    // Sourcing breakdown names
    z.object({ type: z.literal('PcbTotalCost') }),
    z.object({ type: z.literal('OneTimeCost'), data: z.string() }),
    // Individual manufacturing activity or expense, named after itself
    z.object({ type: z.literal('Activity'), data: z.string() }),
]);

// A breakdown item (child row under a line item)
export type BreakdownItemDTO = z.infer<typeof BreakdownItemRuntype>;
export const BreakdownItemRuntype = z.object({
    name: BreakdownItemNameRuntype,
    values: EvaluatedScenarioValuesRuntype,
});

// A line item in an evaluated section
export type EvaluatedCalculationLineItemDTO = z.infer<typeof EvaluatedCalculationLineItemRuntype>;
export const EvaluatedCalculationLineItemRuntype = z.object({
    id: z.string(),
    name: z.string(),
    display_only: z.boolean(),
    note: z.string().nullable(),
    values: EvaluatedScenarioValuesRuntype,
    breakdown: z.array(BreakdownItemRuntype).nullable().optional(),
    display_unit: FormulaDisplayUnitRuntype.optional(),
});

// Line item evaluation error (adjacently tagged: { type, data? })
export type LineItemEvaluationErrorDTO = z.infer<typeof LineItemEvaluationErrorRuntype>;
export const LineItemEvaluationErrorRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('CircularReference'), data: z.array(z.string()) }),
    z.object({ type: z.literal('UndefinedReference'), data: z.string() }),
    z.object({ type: z.literal('DivideByZero') }),
    z.object({ type: z.literal('LogOfNonPositive') }),
    z.object({ type: z.literal('ReversePercentageTooHigh') }),
    z.object({ type: z.literal('CurrencyExchangeError') }),
    z.object({ type: z.literal('DriverEvaluationError'), data: DriverIdRuntype }),
    z.object({ type: z.literal('DriverCountUndefined'), data: DriverIdRuntype }),
    z.object({ type: z.literal('DriverBlocksCheckout'), data: DriverIdRuntype }),
    z.object({ type: z.literal('DependencyFailed'), data: z.string() }),
    z.object({ type: z.literal('ScenarioMismatch') }),
    z.object({ type: z.literal('ManufacturingScenarioNotConfigured') }),
    z.object({
        type: z.literal('BatchSizeNotFound'),
        data: z.object({ batch_size: z.number(), available_batch_sizes: z.array(z.number()) }),
    }),
    z.object({ type: z.literal('SourcingScenarioNotFound'), data: z.string() }),
    z.object({ type: z.literal('ManufacturingScenarioNotFound'), data: z.string() }),
    z.object({ type: z.literal('Internal'), data: z.string() }),
]);

// Result wrapper for each line item in the evaluation response (discriminated union on "type")
export type EvaluatedLineItemResultDTO = z.infer<typeof EvaluatedLineItemResultRuntype>;
export const EvaluatedLineItemResultRuntype = z.discriminatedUnion('type', [
    EvaluatedCalculationLineItemRuntype.extend({ type: z.literal('ok') }),
    z.object({
        type: z.literal('error'),
        id: z.string(),
        name: z.string(),
        error: LineItemEvaluationErrorRuntype,
    }),
]);

// An evaluated section
export type EvaluatedCalculationSectionDTO = z.infer<typeof EvaluatedCalculationSectionRuntype>;
export const EvaluatedCalculationSectionRuntype = z.object({
    id: z.string(),
    name: z.string(),
    line_items: z.array(EvaluatedLineItemResultRuntype),
    note: z.string().nullable(),
});

// An undefined driver count that was evaluated as zero, which happens for RfQs on the automatic
// workflow when the driver doesn't block checkout on error
export type DriverFallbackDTO = z.infer<typeof DriverFallbackRuntype>;
export const DriverFallbackRuntype = z.object({
    driver_id: DriverIdRuntype,
    assembly_id: z.string(),
    sourcing_scenario_id: z.string(),
});

// Full evaluation response
export type EvaluatedCalculationDTO = z.infer<typeof EvaluatedCalculationRuntype>;
export const EvaluatedCalculationRuntype = z.object({
    currency: CurrencyRuntype,
    sections: z.array(EvaluatedCalculationSectionRuntype),
    driver_fallbacks: z.array(DriverFallbackRuntype),
});

// A scenario of a calculation's export: one column pair of its table, with the figures its header shows
export type CalculationExportScenarioDTO = z.infer<typeof CalculationExportScenarioRuntype>;
export const CalculationExportScenarioRuntype = z.object({
    id: z.string(),
    name: z.string(),
    batch_sizes: z.array(z.number()),
    // What the per-unit values divide by; null when the scenario's sourcing scenario was deleted
    order_size: z.number().nullable(),
    // The whole sourcing scenario's availability, shipping included
    sourcing_lead_time: AggregatedAvailabilityRuntype.nullable(),
    // The longest manufacturing lead time of the scenario's assemblies
    manufacturing_lead_time_days: z.number().nullable(),
});

// What a calculation's export shows: the evaluation, frozen or fresh, with the project and scenario figures
export type CalculationExportDTO = z.infer<typeof CalculationExportRuntype>;
export const CalculationExportRuntype = z.object({
    currency: CurrencyRuntype,
    project: z.object({
        id: z.string(),
        name: z.string(),
        customer: z.object({ id: z.string(), name: z.string() }),
    }),
    // Titles the export; null when no exported scenario configures the main assembly
    main_assembly: z.object({ id: z.string(), designator: z.string() }).nullable(),
    scenarios: z.array(CalculationExportScenarioRuntype),
    // In plan order and named as the plan names them now; only variables keep a breakdown
    sections: z.array(EvaluatedCalculationSectionRuntype),
});

// ========================================
// Key-value store types
// ========================================

// DTO for a key-value pair response
export type FlexibleCalculationKeyValueDTO = z.infer<typeof FlexibleCalculationKeyValueRuntype>;
export const FlexibleCalculationKeyValueRuntype = z.object({
    key: z.string(),
    value: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});

// Request body for creating a key-value pair (POST)
export type FlexibleCalculationKeyValueInputDTO = z.infer<typeof FlexibleCalculationKeyValueInputRuntype>;
export const FlexibleCalculationKeyValueInputRuntype = z.object({
    key: z.string(),
    value: z.string(),
});

// Request body for updating a key-value pair (PATCH)
export type FlexibleCalculationKeyValueUpdateDTO = z.infer<typeof FlexibleCalculationKeyValueUpdateRuntype>;
export const FlexibleCalculationKeyValueUpdateRuntype = z.object({
    value: z.string(),
});

// ========================================
// Exported variable types
// ========================================

// Exported variable response DTO
export type ExportedVariableDTO = z.infer<typeof ExportedVariableRuntype>;
export const ExportedVariableRuntype = z.object({
    id: z.string(),
    name: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
});

// Exported variable input DTO (POST)
export type ExportedVariableInputDTO = z.infer<typeof ExportedVariableInputRuntype>;
export const ExportedVariableInputRuntype = z.object({
    name: z.string(),
});

// Exported variable update DTO (PATCH)
export type ExportedVariableUpdateDTO = z.infer<typeof ExportedVariableUpdateRuntype>;
export const ExportedVariableUpdateRuntype = z.object({
    name: z.string().optional(),
});

// ========================================
// Forward declaration for template types (defined fully later)
// ========================================

// Note: FlexibleCalculationTemplateRuntype is defined later in this file
// We use getters below to reference it in a type-safe way

// ========================================
// Plan types (NEW API structure)
// ========================================

// Cost level enum
export type CostLevelDTO = z.infer<typeof CostLevelRuntype>;
export const CostLevelRuntype = z.enum(['unit', 'batch', 'project']);

// Fixed item value
export type FixedItemValueDTO = z.infer<typeof FixedItemValueRuntype>;
export const FixedItemValueRuntype = z.object({
    value: z.number(),
});

// Percentage item value
export type PercentageItemValueDTO = z.infer<typeof PercentageItemValueRuntype>;
export const PercentageItemValueRuntype = z.object({
    fractional_value: z.number(),
});

// Hand-written discriminated union types for FormulaNodeDTO and ComparisonDTO.
// These are needed because z.infer on recursive z.lazy schemas resolves to `any`.
export type FormulaNodeDTO =
    | { type: 'number'; value: number }
    | { type: 'line_item'; reference: string }
    | { type: 'driver'; driver: DriverIdDTO }
    | { type: 'batch_size' }
    | { type: 'order_size' }
    | { type: 'sum'; left: FormulaNodeDTO; right: FormulaNodeDTO }
    | { type: 'subtract'; left: FormulaNodeDTO; right: FormulaNodeDTO }
    | { type: 'multiply'; left: FormulaNodeDTO; right: FormulaNodeDTO }
    | { type: 'divide'; left: FormulaNodeDTO; right: FormulaNodeDTO }
    | { type: 'if'; test: ComparisonDTO; then: FormulaNodeDTO; else_: FormulaNodeDTO }
    | { type: 'round'; operand: FormulaNodeDTO; decimal_places: number }
    | { type: 'log_e'; operand: FormulaNodeDTO }
    | { type: 'floor'; operand: FormulaNodeDTO }
    | { type: 'ceil'; operand: FormulaNodeDTO }
    | { type: 'min'; left: FormulaNodeDTO; right: FormulaNodeDTO }
    | { type: 'max'; left: FormulaNodeDTO; right: FormulaNodeDTO }
    | { type: 'pow'; base: FormulaNodeDTO; exponent: FormulaNodeDTO };

export type ComparisonDTO =
    | { op: 'greater_than'; left: FormulaNodeDTO; right: FormulaNodeDTO }
    | { op: 'greater_than_or_equal'; left: FormulaNodeDTO; right: FormulaNodeDTO }
    | { op: 'less_than'; left: FormulaNodeDTO; right: FormulaNodeDTO }
    | { op: 'less_than_or_equal'; left: FormulaNodeDTO; right: FormulaNodeDTO }
    | { op: 'equal'; left: FormulaNodeDTO; right: FormulaNodeDTO }
    | { op: 'not_equal'; left: FormulaNodeDTO; right: FormulaNodeDTO }
    | { op: 'and'; left: ComparisonDTO; right: ComparisonDTO }
    | { op: 'or'; left: ComparisonDTO; right: ComparisonDTO };

// Formula node types - using z.lazy for recursive schema
export const FormulaNodeRuntype: z.ZodType<FormulaNodeDTO> = z.lazy(() =>
    z.discriminatedUnion('type', [
        z.object({
            type: z.literal('number'),
            value: z.number(),
        }),
        z.object({
            type: z.literal('line_item'),
            reference: z.string(),
        }),
        z.object({
            type: z.literal('driver'),
            driver: DriverIdRuntype,
        }),
        z.object({
            type: z.literal('batch_size'),
        }),
        z.object({
            type: z.literal('order_size'),
        }),
        z.object({
            type: z.literal('sum'),
            left: FormulaNodeRuntype,
            right: FormulaNodeRuntype,
        }),
        z.object({
            type: z.literal('subtract'),
            left: FormulaNodeRuntype,
            right: FormulaNodeRuntype,
        }),
        z.object({
            type: z.literal('multiply'),
            left: FormulaNodeRuntype,
            right: FormulaNodeRuntype,
        }),
        z.object({
            type: z.literal('divide'),
            left: FormulaNodeRuntype,
            right: FormulaNodeRuntype,
        }),
        z.object({
            type: z.literal('if'),
            test: ComparisonRuntype,
            then: FormulaNodeRuntype,
            else_: FormulaNodeRuntype,
        }),
        z.object({
            type: z.literal('round'),
            operand: FormulaNodeRuntype,
            decimal_places: z.number().int(),
        }),
        z.object({
            type: z.literal('log_e'),
            operand: FormulaNodeRuntype,
        }),
        z.object({
            type: z.literal('floor'),
            operand: FormulaNodeRuntype,
        }),
        z.object({
            type: z.literal('ceil'),
            operand: FormulaNodeRuntype,
        }),
        z.object({
            type: z.literal('min'),
            left: FormulaNodeRuntype,
            right: FormulaNodeRuntype,
        }),
        z.object({
            type: z.literal('max'),
            left: FormulaNodeRuntype,
            right: FormulaNodeRuntype,
        }),
        z.object({
            type: z.literal('pow'),
            base: FormulaNodeRuntype,
            exponent: FormulaNodeRuntype,
        }),
    ]),
);

// Comparison operators (mutually recursive with FormulaNodeRuntype)
export const ComparisonRuntype: z.ZodType<ComparisonDTO> = z.lazy(() =>
    z.discriminatedUnion('op', [
        z.object({
            op: z.literal('greater_than'),
            left: FormulaNodeRuntype,
            right: FormulaNodeRuntype,
        }),
        z.object({
            op: z.literal('greater_than_or_equal'),
            left: FormulaNodeRuntype,
            right: FormulaNodeRuntype,
        }),
        z.object({
            op: z.literal('less_than'),
            left: FormulaNodeRuntype,
            right: FormulaNodeRuntype,
        }),
        z.object({
            op: z.literal('less_than_or_equal'),
            left: FormulaNodeRuntype,
            right: FormulaNodeRuntype,
        }),
        z.object({
            op: z.literal('equal'),
            left: FormulaNodeRuntype,
            right: FormulaNodeRuntype,
        }),
        z.object({
            op: z.literal('not_equal'),
            left: FormulaNodeRuntype,
            right: FormulaNodeRuntype,
        }),
        z.object({
            op: z.literal('and'),
            left: ComparisonRuntype,
            right: ComparisonRuntype,
        }),
        z.object({
            op: z.literal('or'),
            left: ComparisonRuntype,
            right: ComparisonRuntype,
        }),
    ]),
);

// Variable line item types
export type SourcingVariableLineItemDTO = z.infer<typeof SourcingVariableLineItemRuntype>;
export const SourcingVariableLineItemRuntype = z.discriminatedUnion('variable', [
    z.object({
        variable: z.literal('total_price'),
        show_pcb_breakdown: z.boolean(),
    }),
    z.object({
        variable: z.literal('unit_price'),
        assembly: z.string(),
        show_pcb_breakdown: z.boolean(),
    }),
    z.object({ variable: z.literal('excess_material_cost'), assembly: z.string() }),
    z.object({ variable: z.literal('scrap_cost'), assembly: z.string() }),
    z.object({
        variable: z.literal('one_time_cost'),
        assembly: z.string(),
        show_one_time_cost_breakdown: z.boolean(),
    }),
    z.object({ variable: z.literal('total_tco_cost'), assembly: z.string() }),
]);

export type ManufacturingVariableLineItemDTO = z.infer<typeof ManufacturingVariableLineItemRuntype>;
export const ManufacturingVariableLineItemRuntype = z.discriminatedUnion('variant', [
    z.object({
        variant: z.literal('unit_costs'),
        assembly_id: z.string(),
        show_process_breakdown: z.boolean(),
    }),
    z.object({
        variant: z.literal('batch_costs'),
        assembly_id: z.string(),
        show_process_breakdown: z.boolean(),
    }),
    z.object({
        variant: z.literal('total_cost'),
        assembly_id: z.string(),
        show_process_breakdown: z.boolean(),
    }),
    z.object({
        variant: z.literal('project_costs'),
        assembly_id: z.string(),
        show_process_breakdown: z.boolean(),
    }),
    z.object({
        variant: z.literal('total_cost_with_project_costs'),
        assembly_id: z.string(),
        show_process_breakdown: z.boolean(),
    }),
]);

// Sourcing variable line item (nested discriminator on 'variable')
const SourcingVariableRuntype = z.discriminatedUnion('variable', [
    z.object({
        variable_type: z.literal('sourcing'),
        variable: z.literal('total_price'),
        assembly: z.string(),
        show_pcb_breakdown: z.boolean(),
    }),
    z.object({
        variable_type: z.literal('sourcing'),
        variable: z.literal('unit_price'),
        assembly: z.string(),
        show_pcb_breakdown: z.boolean(),
    }),
    z.object({
        variable_type: z.literal('sourcing'),
        variable: z.literal('excess_material_cost'),
        assembly: z.string(),
    }),
    z.object({
        variable_type: z.literal('sourcing'),
        variable: z.literal('scrap_cost'),
        assembly: z.string(),
    }),
    z.object({
        variable_type: z.literal('sourcing'),
        variable: z.literal('one_time_cost'),
        assembly: z.string(),
        show_one_time_cost_breakdown: z.boolean(),
    }),
    z.object({
        variable_type: z.literal('sourcing'),
        variable: z.literal('total_tco_cost'),
        assembly: z.string(),
    }),
]);

// Manufacturing variable line item (nested discriminator on 'variable')
const ManufacturingVariableRuntype = z.discriminatedUnion('variable', [
    z.object({
        variable_type: z.literal('manufacturing'),
        variable: z.literal('unit_costs'),
        assembly_id: z.string(),
        show_process_breakdown: z.boolean(),
    }),
    z.object({
        variable_type: z.literal('manufacturing'),
        variable: z.literal('batch_costs'),
        assembly_id: z.string(),
        show_process_breakdown: z.boolean(),
    }),
    z.object({
        variable_type: z.literal('manufacturing'),
        variable: z.literal('total_cost'),
        assembly_id: z.string(),
        show_process_breakdown: z.boolean(),
    }),
    z.object({
        variable_type: z.literal('manufacturing'),
        variable: z.literal('project_costs'),
        assembly_id: z.string(),
        show_process_breakdown: z.boolean(),
    }),
    z.object({
        variable_type: z.literal('manufacturing'),
        variable: z.literal('total_cost_with_project_costs'),
        assembly_id: z.string(),
        show_process_breakdown: z.boolean(),
    }),
]);

// Calculation variable line item
const CalculationVariableRuntype = z.object({
    variable_type: z.literal('calculation'),
    calculation: z.string(),
    scenario_mapping: z.record(z.string(), z.string()),
    exported_variable_id: z.string(),
});

// Combined variable line item - use union since discriminatedUnion doesn't allow duplicate discriminator values in Zod v4
export type VariableLineItemDTO = z.infer<typeof VariableLineItemRuntype>;
export const VariableLineItemRuntype = z.union([
    SourcingVariableRuntype,
    ManufacturingVariableRuntype,
    CalculationVariableRuntype,
]);

// Line item value union type
// Note: Variable types (sourcing, manufacturing, calculation) are now nested under type='variable'
// with a second discriminator 'variable_type' for the specific variant
export type LineItemValueDTO = z.infer<typeof LineItemValueRuntype>;
export const LineItemValueRuntype = z.union([
    z.object({
        type: z.literal('fixed'),
        level: CostLevelRuntype,
        assembly: z.string().nullable().optional(),
        values: z.record(z.string(), FixedItemValueRuntype), // Map<ScenarioId, FixedItemValue>
        display_unit: FormulaDisplayUnitRuntype.optional(),
    }),
    z.object({
        type: z.literal('percentage'),
        reference_items: z.array(z.string()),
        values: z.record(z.string(), PercentageItemValueRuntype), // Map<ScenarioId, PercentageItemValue>
    }),
    z.object({
        type: z.literal('reverse_percentage'),
        reference_item: z.string(), // CalculationLineItemId - UUID of the subtotal this item references
        values: z.record(z.string(), PercentageItemValueRuntype), // Map<ScenarioId, PercentageItemValue>
    }),
    z.object({
        type: z.literal('subtotal'),
        scope: z.union([z.literal('current_section'), z.literal('all_sections')]),
        range: z.union([z.literal('from_previous_subtotal'), z.literal('from_beginning')]),
    }),
    z.object({
        type: z.literal('formula'),
        level: CostLevelRuntype,
        assembly: z.string().nullable(),
        formula: FormulaNodeRuntype,
        display_unit: FormulaDisplayUnitRuntype,
    }),
    // Variable types wrapped with type='variable' and discriminated by 'variable_type'
    z.object({ type: z.literal('variable') }).and(VariableLineItemRuntype),
    z.object({
        type: z.literal('notes'),
    }),
]);

// Line item
export type CalculationPlanLineItemDTO = z.infer<typeof CalculationPlanLineItemRuntype>;
export const CalculationPlanLineItemRuntype = z.object({
    id: z.string(),
    name: z.string(),
    display_only: z.boolean(),
    note: z.string().nullable(),
    value: LineItemValueRuntype,
    lock_mode: LockModeRuntype,
    exported_variable_id: z.string().nullable(),
});

// Section
export type CalculationPlanSectionDTO = z.infer<typeof CalculationPlanSectionRuntype>;
export const CalculationPlanSectionRuntype = z.object({
    id: z.string(),
    name: z.string(),
    line_items: z.array(CalculationPlanLineItemRuntype),
    note: z.string().nullable(),
    lock_mode: LockModeRuntype,
});

// Assembly scenario configuration
export type AssemblyScenarioConfigurationDTO = z.infer<typeof AssemblyScenarioConfigurationRuntype>;
export const AssemblyScenarioConfigurationRuntype = z.object({
    assembly_id: z.string(),
    sourcing_scenario: z.string(),
    manufacturing_scenario: z.string().nullable(),
    batch_size: z.number().int(),
});

// Calculation scenario
export type CalculationScenarioDTO = z.infer<typeof CalculationScenarioRuntype>;
export const CalculationScenarioRuntype = z.object({
    id: z.string(),
    name: z.string(),
    assembly_configurations: z.array(AssemblyScenarioConfigurationRuntype),
});

// Request body for creating a plan (POST)
export type CreateFlexibleCalculationPlanDTO = z.infer<typeof CreateFlexibleCalculationPlanRuntype>;
export const CreateFlexibleCalculationPlanRuntype = z.object({
    name: z.string(),
    rfq_id: z.string(),
    currency: CurrencyRuntype,
    main_assembly: z.string(),
    scenarios: z.array(CalculationScenarioRuntype),
    sections: z.array(CalculationPlanSectionRuntype),
    get raw_template() {
        return FlexibleCalculationTemplateRuntype;
    },
    selected_template: z.string(),
});

// Request body for creating a plan from a template (POST /plans/from-template)
export type CalculationReferenceInputDTO = z.infer<typeof CalculationReferenceInputRuntype>;
export const CalculationReferenceInputRuntype = z.object({
    calculation: z.string(),
    scenario_mapping: z.record(z.string(), z.string()),
});

export type CreateFlexibleCalculationPlanFromTemplateDTO = z.infer<
    typeof CreateFlexibleCalculationPlanFromTemplateRuntype
>;
export const CreateFlexibleCalculationPlanFromTemplateRuntype = z.object({
    template_id: z.string(),
    rfq_id: z.string(),
    name: z.string(),
    currency: CurrencyRuntype,
    main_assembly: z.string(),
    scenarios: z.array(CalculationScenarioRuntype),
    selected_template: z.string(),
    // Each exported variable maps to a list of bindings
    calculation_references: z.record(z.string(), z.array(CalculationReferenceInputRuntype)),
});

// Request body for updating a plan (PATCH)
export type UpdateFlexibleCalculationPlanDTO = z.infer<typeof UpdateFlexibleCalculationPlanRuntype>;
export const UpdateFlexibleCalculationPlanRuntype = z.object({
    id: z.string(), // Required in body per Rust CalculationPlanUpdateDto
    name: z.string().optional(),
    plan_value: z
        .object({
            currency: CurrencyRuntype,
            main_assembly: z.string(),
            scenarios: z.array(CalculationScenarioRuntype),
            sections: z.array(CalculationPlanSectionRuntype),
            frozen: z.boolean(),
            get raw_template() {
                return FlexibleCalculationTemplateRuntype;
            },
            selected_template: z.string(),
            frozen_result: EvaluatedCalculationRuntype.optional().nullable(),
        })
        .optional(),
});

// DTO for a plan response (GET)
export type FlexibleCalculationPlanDTO = z.infer<typeof FlexibleCalculationPlanRuntype>;
export const FlexibleCalculationPlanRuntype = z.object({
    id: z.string(),
    name: z.string(),
    rfq_id: z.string(),
    currency: CurrencyRuntype,
    main_assembly: z.string(),
    scenarios: z.array(CalculationScenarioRuntype),
    sections: z.array(CalculationPlanSectionRuntype),
    frozen: z.boolean(),
    get raw_template() {
        return FlexibleCalculationTemplateRuntype;
    },
    selected_template: z.string(),
    frozen_result: EvaluatedCalculationRuntype.optional().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});

// ========================================
// Template types (REST API)
// ========================================

// Subtotal scope and range enums (matching backend)
export type SubtotalScopeDTO = z.infer<typeof SubtotalScopeRuntype>;
export const SubtotalScopeRuntype = z.union([z.literal('current_section'), z.literal('all_sections')]);

export type SubtotalRangeDTO = z.infer<typeof SubtotalRangeRuntype>;
export const SubtotalRangeRuntype = z.union([z.literal('from_previous_subtotal'), z.literal('from_beginning')]);

// Template variable line item - excludes assembly/scenario references
// Templates include all variable types but without assembly_id/assembly/scenario references
const TemplateCalculationVariableRuntype = z.object({
    variable_type: z.literal('calculation'),
    id: z.string(), // The exported variable ID (matches Rust field name `id: CalculationExportedVariableId`)
});

const TemplateSourcingVariableRuntype = z.discriminatedUnion('variable', [
    z.object({
        variable_type: z.literal('sourcing'),
        variable: z.literal('total_price'),
        show_pcb_breakdown: z.boolean(),
    }),
    z.object({
        variable_type: z.literal('sourcing'),
        variable: z.literal('unit_price'),
        show_pcb_breakdown: z.boolean(),
    }),
    z.object({ variable_type: z.literal('sourcing'), variable: z.literal('excess_material_cost') }),
    z.object({ variable_type: z.literal('sourcing'), variable: z.literal('scrap_cost') }),
    z.object({
        variable_type: z.literal('sourcing'),
        variable: z.literal('one_time_cost'),
        show_one_time_cost_breakdown: z.boolean(),
    }),
    z.object({ variable_type: z.literal('sourcing'), variable: z.literal('total_tco_cost') }),
]);

const TemplateManufacturingVariableRuntype = z.discriminatedUnion('variable', [
    z.object({
        variable_type: z.literal('manufacturing'),
        variable: z.literal('unit_costs'),
        show_process_breakdown: z.boolean(),
    }),
    z.object({
        variable_type: z.literal('manufacturing'),
        variable: z.literal('batch_costs'),
        show_process_breakdown: z.boolean(),
    }),
    z.object({
        variable_type: z.literal('manufacturing'),
        variable: z.literal('project_costs'),
        show_process_breakdown: z.boolean(),
    }),
    z.object({
        variable_type: z.literal('manufacturing'),
        variable: z.literal('total_cost'),
        show_process_breakdown: z.boolean(),
    }),
    z.object({
        variable_type: z.literal('manufacturing'),
        variable: z.literal('total_cost_with_project_costs'),
        show_process_breakdown: z.boolean(),
    }),
]);

// Combined variable line item for templates - all variable types but without assembly/scenario references
export type VariableLineItemTemplateDTO = z.infer<typeof VariableLineItemTemplateRuntype>;
export const VariableLineItemTemplateRuntype = z.union([
    TemplateSourcingVariableRuntype,
    TemplateManufacturingVariableRuntype,
    TemplateCalculationVariableRuntype,
]);

// Template line item value union type
export type TemplateLineItemValueDTO = z.infer<typeof TemplateLineItemValueRuntype>;
export const TemplateLineItemValueRuntype = z.union([
    z.object({
        type: z.literal('fixed'),
        level: CostLevelRuntype,
        value: FixedItemValueRuntype,
        display_unit: FormulaDisplayUnitRuntype.optional(),
        // When true, deriving a calculation repeats this line item once per assembly.
        // Defaults to false for older payloads that omit it.
        repeat_per_assembly: z.boolean().optional().default(false),
    }),
    z.object({
        type: z.literal('percentage'),
        reference_items: z.array(z.string()), // Set<CalculationTemplateLineItemId>
        value: PercentageItemValueRuntype,
    }),
    z.object({
        type: z.literal('reverse_percentage'),
        reference_item: z.string(), // CalculationTemplateLineItemId
        value: PercentageItemValueRuntype,
    }),
    z.object({
        type: z.literal('subtotal'),
        scope: SubtotalScopeRuntype,
        range: SubtotalRangeRuntype,
    }),
    z.object({
        type: z.literal('formula'),
        level: CostLevelRuntype,
        formula: FormulaNodeRuntype, // Formula with CalculationTemplateLineItemId references
        display_unit: FormulaDisplayUnitRuntype,
        repeat_per_assembly: z.boolean().optional().default(false),
    }),
    z
        .object({
            type: z.literal('variable'),
        })
        .and(VariableLineItemTemplateRuntype),
    z.object({
        type: z.literal('notes'),
    }),
]);

// Template line item
export type CalculationTemplateLineItemDTO = z.infer<typeof CalculationTemplateLineItemRuntype>;
export const CalculationTemplateLineItemRuntype = z.object({
    id: z.string(), // CalculationTemplateLineItemId (UUID)
    name: z.string(),
    display_only: z.boolean(),
    note: z.string().nullable(),
    value: TemplateLineItemValueRuntype,
    lock_mode: LockModeRuntype,
    exported_variable_id: z.string().nullable(),
});

// Template section
export type CalculationTemplateSectionDTO = z.infer<typeof CalculationTemplateSectionRuntype>;
export const CalculationTemplateSectionRuntype = z.object({
    id: z.string(), // CalculationTemplateSectionId (UUID)
    name: z.string(),
    line_items: z.array(CalculationTemplateLineItemRuntype),
    note: z.string().nullable(),
    lock_mode: LockModeRuntype,
});

// Template DTO (for GET responses)
export type FlexibleCalculationTemplateDTO = z.infer<typeof FlexibleCalculationTemplateRuntype>;
export const FlexibleCalculationTemplateRuntype = z.object({
    id: z.string(), // FlexibleCalculationTemplateId (UUID)
    name: z.string(),
    currency: CurrencyRuntype,
    child_assemblies_as_line_items: z.boolean(),
    sections: z.array(CalculationTemplateSectionRuntype),
    created_at: z.string(),
    updated_at: z.string(),
});

// Template input DTO (for POST)
export type FlexibleCalculationTemplateInputDTO = z.infer<typeof FlexibleCalculationTemplateInputRuntype>;
export const FlexibleCalculationTemplateInputRuntype = z.object({
    name: z.string(),
    currency: CurrencyRuntype,
    child_assemblies_as_line_items: z.boolean(),
    sections: z.array(CalculationTemplateSectionRuntype),
});

// Template update DTO (for PATCH)
export type FlexibleCalculationTemplateUpdateDTO = z.infer<typeof FlexibleCalculationTemplateUpdateRuntype>;
export const FlexibleCalculationTemplateUpdateRuntype = z.object({
    name: z.string(),
    currency: CurrencyRuntype,
    child_assemblies_as_line_items: z.boolean(),
    sections: z.array(CalculationTemplateSectionRuntype),
});

// Validation response types
const ValidateCalculationTemplateSuccessRuntype = z.object({
    result: z.literal('Success'),
});

const ValidateCalculationTemplateErrorRuntype = z.object({
    result: z.literal('Error'),
    error: z.enum([
        'CircularReference',
        'UndefinedReference',
        'InvalidReversePercentageReference',
        'DuplicateLineItemId',
    ]),
    data: z.unknown(),
});

export type ValidateCalculationTemplateResponseDTO = z.infer<typeof ValidateCalculationTemplateResponseRuntype>;
export const ValidateCalculationTemplateResponseRuntype = z.discriminatedUnion('result', [
    ValidateCalculationTemplateSuccessRuntype,
    ValidateCalculationTemplateErrorRuntype,
]);

// Plan validation response types
const ValidateCalculationPlanSuccessRuntype = z.object({
    result: z.literal('Success'),
});

const ValidateCalculationPlanErrorItemRuntype = z.discriminatedUnion('error', [
    z.object({
        error: z.literal('CircularReference'),
        data: z.array(z.tuple([z.string(), z.string()])), // [calculationId, lineItemId][]
    }),
    z.object({
        error: z.literal('UndefinedReference'),
        data: z.object({
            line_item_id: z.string(),
            referenced_item: z.string(),
        }),
    }),
    z.object({
        error: z.literal('UndefinedExportedVariableReference'),
        data: z.object({
            line_item_id: z.string(),
            referenced_calculation: z.string(),
            exported_variable_id: z.string(),
        }),
    }),
    z.object({
        error: z.literal('CalculationNotFound'),
        data: z.object({
            line_item_id: z.string(),
            calculation_id: z.string(),
        }),
    }),
]);

export type ValidateCalculationPlanErrorItemDTO = z.infer<typeof ValidateCalculationPlanErrorItemRuntype>;

const ValidateCalculationPlanErrorRuntype = z.object({
    result: z.literal('Error'),
    errors: z.array(ValidateCalculationPlanErrorItemRuntype),
});

export type ValidateCalculationPlanResponseDTO = z.infer<typeof ValidateCalculationPlanResponseRuntype>;
export const ValidateCalculationPlanResponseRuntype = z.discriminatedUnion('result', [
    ValidateCalculationPlanSuccessRuntype,
    ValidateCalculationPlanErrorRuntype,
]);

// ========================================
// Legacy plan data types (for backward compatibility)
// ========================================

// Legacy DTO for plan data stored in JSONB (old format - kept for migration)
export type LegacyFlexibleCalculationPlanDataDTO = z.infer<typeof LegacyFlexibleCalculationPlanDataRuntype>;
export const LegacyFlexibleCalculationPlanDataRuntype = z.object({
    rawTemplate: z.unknown(),
    expandedPlan: z.unknown(),
    scenarioConfigurations: z.array(z.unknown()),
    topLevelAssemblyId: z.string(),
    currency: z.string(),
    selectedTemplate: z.string(),
    frozen: z.boolean().optional().default(false),
    frozenResult: z.unknown().nullable().optional(),
});
